import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { useRowNumber } from '../../../../page-properties/bulksheet-properties/rowNumber'
import { dragAndDrop } from '../../../../page-properties/bulksheet-properties/dragAndDrop'
import {
  useCreatedBy,
  useCreatedAt,
  useUpdatedBy,
  useUpdatedAt,
} from '../../../../page-properties/bulksheet-properties/changeLog'
import { useMemo } from 'react'
import { RoleRow, roleRowService } from '../../model/RoleRow'
import { PermissionPolicyOption } from '../../../../../domain/value-object/PermissionPolicy'
import { cellStyle } from './cellStyle'
import { useAvailableFunctionsCellRenderer } from './availableFunctionsCellRenderer'
import { useCode } from '../../../../page-properties/bulksheet-properties/common'
import { usePermissionPolicies } from '../../../../page-properties/bulksheet-properties/permissionPolicies'

type UseColumnDefsProps = {
  permissionPolicyOptions: PermissionPolicyOption[]
  allPermissionPolicyOptions: PermissionPolicyOption[]
}

export const useColumnDefs = ({
  permissionPolicyOptions,
  allPermissionPolicyOptions,
}: UseColumnDefsProps): (ColDef | ColGroupDef)[] => {
  const codeCol = useCode('code', { cellStyle })
  const rowNumberCol = useRowNumber()
  const createdByCol = useCreatedBy('createdBy')
  const createdAtCol = useCreatedAt('createdAt')
  const updatedByCol = useUpdatedBy('updatedBy')
  const updatedAtCol = useUpdatedAt('updatedAt')
  const permissionPoliciesColSetting = useMemo(() => {
    return {
      editable: params => params.data && roleRowService.isEditable(params.data),
      cellStyle,
      cellRendererParams: {
        validate: (value: string[], params: ICellRendererParams<RoleRow>) => {
          if (!params.data?.edited) return
          if (!value || value.length === 0) {
            return intl.formatMessage({ id: 'boolExpression.requiredField' })
          }

          // Check if all attachedByDefault policies are included
          const missingDefaultPolicy = permissionPolicyOptions.some(
            p => p.attachedByDefault && !value.includes(p.uuid)
          )
          if (missingDefaultPolicy) {
            return intl.formatMessage({
              id: 'roles.validation.attachedByDefaultRequired',
            })
          }

          // Check if any non-attachable policies are included
          const hasNonAttachablePolicy = permissionPolicyOptions.some(
            p => !p.attachable && value.includes(p.uuid)
          )
          if (hasNonAttachablePolicy) {
            return intl.formatMessage({
              id: 'roles.validation.nonAttachablePolicyIncluded',
            })
          }

          return undefined
        },
      },
    }
  }, [permissionPolicyOptions])
  const permissionPoliciesCol = usePermissionPolicies(
    'permissionPolicyUuids',
    permissionPolicyOptions,
    allPermissionPolicyOptions,
    permissionPoliciesColSetting
  )
  const availableFunctionsCellRenderer = useAvailableFunctionsCellRenderer({
    permissionPolicyOptions: allPermissionPolicyOptions,
  })

  const columnDef = useMemo(
    () => [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      dragAndDrop,
      rowNumberCol,
      codeCol,
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'roles.column.name' }),
        editable: params =>
          params.data && roleRowService.isEditable(params.data),
        width: 200,
        floatingFilter: true,
        cellRendererParams: {
          validate: (value?: string) => {
            if (!value || value.trim() === '') {
              return intl.formatMessage({ id: 'boolExpression.requiredField' })
            }
            return undefined
          },
        },
        cellStyle,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'roles.column.description' }),
        editable: params =>
          params.data && roleRowService.isEditable(params.data),
        width: 300,
        floatingFilter: true,
        cellStyle,
      },
      permissionPoliciesCol,
      {
        field: 'availableFunctions',
        headerName: intl.formatMessage({
          id: 'roles.column.availableFunctions',
        }),
        editable: false,
        width: 100,
        cellRenderer: availableFunctionsCellRenderer,
        cellStyle,
      },
      {
        field: 'inUse',
        headerName: intl.formatMessage({ id: 'roles.column.inUse' }),
        editable: false,
        width: 100,
        valueFormatter: params => {
          return params.value
            ? intl.formatMessage({ id: 'roles.inUse.true' })
            : ''
        },
        cellStyle,
      },
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [createdByCol, createdAtCol, updatedByCol, updatedAtCol],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      permissionPoliciesCol,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
    ]
  )

  return columnDef
}
